import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Archive,
  Building2,
  CheckCircle2,
  Clock3,
  Pause,
  PauseCircle,
  Play,
  Plus,
  Trash2,
} from 'lucide-react'
import { CountryFilter } from '../../components/ui/CountryFilter'
import { CountryCell } from '../../components/ui/CountryCell'
import { Breadcrumb } from '../../components/Breadcrumb'
import { DataTable } from '../../components/table/DataTable'
import { api, extractApiError } from '../../lib/api'
import {
  platformTenantsResponse,
  platformUsersResponse,
  plansResponse,
} from '../../lib/types'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Avatar } from '../../components/Avatar'
import { RolePill, StatusPill } from '../../components/ui/Pills'
import { FilterRow } from '../../components/filters/FilterRow'
import { TenantRowActions } from '../../components/TenantRowActions'
import { TenantCreateDialog } from '../../components/TenantCreateDialog'
import { formatDateTime, formatNumber } from '../../lib/format'
import { usePageSize } from '../../lib/display-prefs'

/** Compact byte formatting — "12.4 GB", "850 MB", "0 B". Same rules
 *  the Storage page uses so the two reads stay aligned. */
function fmtBytes(bytes) {
  const v = Number(bytes ?? 0)
  if (v === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(units.length - 1, Math.floor(Math.log(v) / Math.log(1024)))
  const scaled = v / Math.pow(1024, i)
  const digits = scaled < 10 && i > 0 ? 1 : 0
  return `${formatNumber(scaled, { minimumFractionDigits: digits, maximumFractionDigits: digits, useGrouping: false })} ${units[i]}`
}

/** Compact token formatting — "1.2M", "12.3k", "850". Same rules the
 *  AI Usage page uses so the per-company figure in this column reads
 *  consistently with that page's per-tenant table. */
function fmtTokens(n) {
  const v = Number(n ?? 0)
  if (v >= 1_000_000) return `${formatNumber(v / 1_000_000, { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false })}M`
  if (v >= 1_000) return `${formatNumber(v / 1_000, { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false })}k`
  return String(v)
}

/** Origin / provenance pill for the Companies table.
 *
 *  Reads `t.createdVia` from the Tenant row (added 2026-05-22) and
 *  renders a coloured pill so the Super Admin can tell at a glance
 *  which tenants signed up themselves vs. were provisioned by hand
 *  vs. seeded by the demo-data scripts. Defaults to "Self signup"
 *  for older rows that pre-date the field — the model's default
 *  matches, so this is just defence-in-depth for unparsed payloads.
 */
function OriginCell({ createdVia }) {
  const v = String(createdVia ?? 'self_signup')
  // Self signup reads on the green tint; every other origin (Admin
  // created, Demo seed) reads on the rose tint. Background / text /
  // border tones match the dashboard emerald + rose chips.
  let label = 'Self signup'
  let cls = 'bg-[#D1FAE5] text-[#059669] border-[#A7F3D0] dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10'
  if (v === 'super_admin') {
    label = 'Admin created'
    cls = 'bg-[#FFE4E6] text-[#E11D48] border-[#FECDD3] dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10'
  } else if (v === 'demo_seed') {
    label = 'Demo seed'
    cls = 'bg-[#FFE4E6] text-[#E11D48] border-[#FECDD3] dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10'
  }
  return (
    <span
      className={
        'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border whitespace-nowrap ' +
        cls
      }
    >
      {label}
    </span>
  )
}

/** Per-tone tile + halo pair for the Companies-page KPI strip. Lifted
 *  verbatim from `SuperAdminDashboard.jsx`'s TONE_CLASSES (the four
 *  shades we actually use here) so the two surfaces share the exact
 *  same vivid mid-tone-on-tint look. If you tweak the dashboard
 *  palette, mirror the change here so the two pages don't drift. */
const KPI_TONES = {
  violet: {
    tile: 'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10',
    halo: 'from-[#EDE9FE] to-transparent dark:from-transparent',
    stroke: '#7C3AED',
  },
  emerald: {
    tile: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
    halo: 'from-[#D1FAE5] to-transparent dark:from-transparent',
    stroke: '#059669',
  },
  rose: {
    tile: 'bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10',
    halo: 'from-[#FFE4E6] to-transparent dark:from-transparent',
    stroke: '#E11D48',
  },
  sky: {
    tile: 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10',
    halo: 'from-[#DBEAFE] to-transparent dark:from-transparent',
    stroke: '#2563EB',
  },
  amber: {
    tile: 'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA] dark:bg-white/[0.06] dark:text-orange-300 dark:border-white/10',
    halo: 'from-[#FFEDD5] to-transparent dark:from-transparent',
    stroke: '#EA580C',
  },
}

// `smoothPath` + `Sparkline` helpers removed along with the in-card
// 14-day trend curve. Restore from git history if a trend overlay
// ever needs to come back to these cards.

/** Single-stat KPI card matching the dashboard's `KpiHeroTile` shape
 *  beat-for-beat — rounded-xl chrome, blurred halo bleed in the top-
 *  right, 12×12 tinted icon chip, 4xl font-display value, uppercase
 *  tracking-wider label, optional muted hint underneath. Drops the
 *  sparkline + delta + "View →" link the dashboard tile has because
 *  the Companies-page summary doesn't carry trend data and the table
 *  below already IS the click-through target. */
function KpiCard({ label, value, icon: Icon, tone = 'violet', hint }) {
  const tones = KPI_TONES[tone] ?? KPI_TONES.violet
  return (
    // Sparkline removed across all KPI cards (the soft trend curve at
    // the bottom of each card competed visually with the headline
    // number). `pb-14` + `min-h-[144px]` were buffers for that curve;
    // tiles now hug their content with plain `p-5`.
    <div className="flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 transition-all duration-200 hover:border-[color:var(--color-accent-border)] hover:shadow-lg hover:-translate-y-0.5">
      <div
        aria-hidden="true"
        className={
          'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' +
          tones.halo
        }
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0">
            {label}
          </h3>
          <p className="mt-2 font-display text-3xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
            {formatNumber(Number(value ?? 0))}
          </p>
          {hint ? (
            <p className="mt-2 text-[13px] text-[color:var(--color-ink-muted)]">
              {hint}
            </p>
          ) : null}
        </div>
        <div
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' +
            tones.tile
          }
        >
          <Icon size={24} />
        </div>
      </div>
    </div>
  )
}

// Note: `ALL_COUNTRIES` + the `CountryFilter` component were
// extracted to `src/components/ui/CountryFilter.jsx` so the AI
// Usage and Storage pages can reuse the same picker. Imported at
// the top of this file — see `CountryFilter` JSX in the filter row
// below.

// `CountryFilter` is now imported from
// `src/components/ui/CountryFilter.jsx` — see the top of this file.
// The inlined version that used to live here was lifted verbatim to
// the shared module so the AI Usage + Storage pages can use the
// same dropdown without copy-paste drift.
/** Platform Admin "Tenants" — list every customer organisation on the
 *  platform. Plan tier + feature toggles + member counts visible at a
 *  glance; click into a row for full detail + actions. */
export function TenantsPage() {
  const [search, setSearch] = useState('')
  const [planFilter, setPlanFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  // Country filter — added alongside Status and Plan after the
  // earlier "By Country" KPI card was removed. The card was a static
  // breakdown; the filter is the productive equivalent — the Master
  // Admin clicks one country and the table narrows to just those
  // tenants. Values map to billingCurrency on the backend (India →
  // INR, UAE → AED, Global → USD).
  const [countryFilter, setCountryFilter] = useState('all')
  const [page, setPage] = useState(1)
  // Starts at (and follows) the account rows-per-page (My Preferences);
  // this list's API accepts every standard size up to 500.
  const [limit, setLimit] = usePageSize()
  const [createOpen, setCreateOpen] = useState(false)
  // Company whose users are shown in the popup (set by clicking the
  // "Users" count). `{ id, name }` while open, null when closed.
  const [usersTenant, setUsersTenant] = useState(null)
  // Drives the brief amber highlight on a focused row (e.g. arriving
  // from the dashboard's Investigate link). `null` when no row is
  // currently highlighted; a string tenant id while the highlight
  // animation is active.
  const [focusedId, setFocusedId] = useState(null)
  const [searchParams, setSearchParams] = useSearchParams()
  // Guards against React 18 StrictMode double-invocation re-opening
  // the dialog after the user closes it on the first render.
  const handledActionRef = useRef(false)

  // Shared server params for the active Search + Status + Country + Plan
  // filters — used by both the paged table query and the export "all
  // records" fetch so a CSV always mirrors exactly what the filters show.
  function filterParams() {
    const params = {}
    if (search.trim()) params.search = search.trim()
    if (planFilter !== 'all') params.plan = planFilter
    if (statusFilter !== 'all') params.status = statusFilter
    if (countryFilter !== 'all') params.country = countryFilter
    return params
  }

  const tenantsQuery = useQuery({
    queryKey: [
      'platform-tenants',
      search,
      planFilter,
      statusFilter,
      countryFilter,
      page,
      limit,
    ],
    queryFn: async () => {
      const res = await api.get('/super-admin/tenants', {
        params: { ...filterParams(), page, limit },
      })
      return platformTenantsResponse.parse(res.data)
    },
    placeholderData: (prev) => prev,
  })

  // Plan catalogue powers the filter dropdown — was a fixed enum
  // (`flowdesk_only` / `flowdesk_plus_sop`) before the Plans page
  // could create custom plans. Now we list whatever exists.
  const plansQuery = useQuery({
    queryKey: ['platform-plans-dropdown'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await api.get('/super-admin/plans')
      return plansResponse.parse(res.data)
    },
  })

  const rawRows = tenantsQuery.data?.data ?? []
  const pageInfo = tenantsQuery.data?.pagination ?? null
  // DataTable sorts the current page internally (via each column's
  // `sortAccessor`); this alias keeps the deep-link focus effect below,
  // which only needs row membership (sort order is irrelevant to it).
  const rows = rawRows

  const qc = useQueryClient()

  // ── Bulk company operations (Master-Admin) ────────────────────────────
  // Fan out to the SAME per-company endpoints the row ⋮ menu uses, so every
  // server-side guard, audit entry and session-termination still applies per
  // company. Rows we KNOW are inapplicable are skipped; anything on another
  // page (status unknown here) is attempted and the server is the guard.
  const BULK = {
    suspend: {
      verb: 'Suspend', done: 'suspended', danger: false, reason: true,
      applies: (s) => s === 'active',
      run: (id, reason) =>
        api.patch(`/super-admin/tenants/${id}/status`, {
          status: 'suspended', reason: reason || undefined,
        }),
    },
    reactivate: {
      verb: 'Reactivate', done: 'reactivated', danger: false, reason: false,
      applies: (s) => s === 'suspended',
      run: (id) =>
        api.patch(`/super-admin/tenants/${id}/status`, { status: 'active' }),
    },
    offboard: {
      verb: 'Offboard', done: 'offboarded', danger: true, reason: true,
      applies: (s) => s !== 'offboarded',
      run: (id, reason) =>
        api.patch(`/super-admin/tenants/${id}/status`, {
          status: 'offboarded', reason: reason || undefined,
        }),
    },
    delete: {
      verb: 'Delete', done: 'deleted', danger: true, reason: false,
      applies: () => true,
      run: (id) => api.delete(`/super-admin/tenants/${id}`),
    },
  }
  const [bulk, setBulk] = useState(null) // { type, ids, clearSelection } | null
  const [bulkReason, setBulkReason] = useState('')
  const [bulkRunning, setBulkRunning] = useState(false)
  const statusById = useMemo(
    () => Object.fromEntries(rows.map((r) => [r.id, r.status])),
    [rows],
  )
  function openBulk(type, ids, ctx) {
    setBulkReason('')
    setBulk({ type, ids, clearSelection: ctx?.clearSelection })
  }
  function closeBulk() {
    if (bulkRunning) return
    setBulk(null)
    setBulkReason('')
  }
  const bulkActions = [
    { label: 'Suspend', icon: Pause, onClick: (ids, ctx) => openBulk('suspend', ids, ctx) },
    { label: 'Reactivate', icon: Play, onClick: (ids, ctx) => openBulk('reactivate', ids, ctx) },
    // Offboard is styled like Suspend (not red like Delete) — matching the
    // per-company row menu; its confirm dialog still carries the terminal
    // warning + red confirm (BULK.offboard.danger).
    { label: 'Offboard', icon: Archive, onClick: (ids, ctx) => openBulk('offboard', ids, ctx) },
    { label: 'Delete', icon: Trash2, danger: true, onClick: (ids, ctx) => openBulk('delete', ids, ctx) },
  ]
  async function runBulk() {
    const cfg = BULK[bulk.type]
    // Skip only rows we KNOW are inapplicable; unknown (other pages) are tried.
    const ids = bulk.ids.filter(
      (id) => statusById[id] == null || cfg.applies(statusById[id]),
    )
    if (ids.length === 0) {
      toast.message('Nothing to do — the action doesn’t apply to the selected companies.')
      closeBulk()
      return
    }
    setBulkRunning(true)
    let ok = 0
    const fails = []
    for (const id of ids) {
      try {
        await cfg.run(id, bulkReason.trim())
        ok += 1
      } catch (e) {
        fails.push(extractApiError(e).message)
      }
    }
    setBulkRunning(false)
    const skipped = bulk.ids.length - ids.length
    if (ok)
      toast.success(
        `${ok} ${ok === 1 ? 'company' : 'companies'} ${cfg.done}.` +
          (skipped ? ` ${skipped} skipped.` : ''),
      )
    if (fails.length)
      toast.error(
        `${fails.length} failed: ${fails[0]}` +
          (fails.length > 1 ? ` (+${fails.length - 1} more)` : ''),
      )
    await qc.invalidateQueries({ queryKey: ['platform-tenants'] })
    bulk.clearSelection?.()
    setBulk(null)
    setBulkReason('')
  }

  // Quick-action handoff from the dashboard: `?action=create` flips
  // the Create dialog open on mount, then strips the param so a
  // refresh or back-button traversal doesn't re-open it.
  useEffect(() => {
    if (handledActionRef.current) return
    if (searchParams.get('action') !== 'create') return
    handledActionRef.current = true
    setCreateOpen(true)
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('action')
        return next
      },
      { replace: true },
    )
  }, [searchParams, setSearchParams])

  // Focus handoff from the dashboard's "Investigate" link: scroll the
  // matching row into view and flash an amber ring for ~2 seconds.
  // Waits until the row is in the DOM (tenants query may still be
  // resolving on first mount). The `focus` param is cleared once the
  // highlight fades so a refresh / back-button doesn't re-flash.
  useEffect(() => {
    const focusId = searchParams.get('focus')
    if (!focusId) return
    if (rows.length === 0) return
    if (!rows.some((r) => r.id === focusId)) return
    const row = document.querySelector(`[data-tenant-id="${focusId}"]`)
    if (!row) return
    row.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setFocusedId(focusId)
    const fadeTimer = setTimeout(() => {
      setFocusedId(null)
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete('focus')
          return next
        },
        { replace: true },
      )
    }, 2000)
    return () => clearTimeout(fadeTimer)
  }, [rows, searchParams, setSearchParams])
  // Search / plan / status filtering now runs server-side.
  // Tenant rollup for the KPI card strip. Defaulted here so first-
  // paint (before the query resolves) renders the cards with zeroes
  // instead of nulls. `sparklines` arrives as a 14-day series per
  // segment from the backend `$facet` aggregation; fall back to an
  // empty array so the Sparkline component renders nothing instead
  // of crashing on a null read.
  const summary = tenantsQuery.data?.summary ?? {
    total: 0,
    active: 0,
    inactive: 0,
    trial: 0,
    sparklines: { total: [], active: [], inactive: [], trial: [] },
  }
  const planOptions = useMemo(() => {
    const list = plansQuery.data?.data ?? []
    return [...list]
      // Inactive ("Hidden") plans are drafts — keep them out of the
      // filter so they stay invisible everywhere until activated.
      .filter((p) => p.isActive)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
      .map((p) => ({ value: p.code, label: p.name }))
  }, [plansQuery.data])

  // A filter is "active" when it differs from its default (empty search
  // or the 'all' sentinel). Drives the Clear-all-filters button, which
  // only appears once at least one filter is narrowing the table.
  const hasAnyFilter =
    search.trim() !== '' ||
    statusFilter !== 'all' ||
    countryFilter !== 'all' ||
    planFilter !== 'all'

  function clearFilters() {
    setSearch('')
    setStatusFilter('all')
    setCountryFilter('all')
    setPlanFilter('all')
    setPage(1)
  }

  // Export "all records" pages through the filtered feed (bounded to keep a
  // huge roster from stalling the browser); "current page"/"selected" fall
  // back to what DataTable already holds.
  async function fetchAllForExport(scope, ids) {
    const all = []
    for (let p = 1; p <= 50; p += 1) {
      const res = await api.get('/super-admin/tenants', {
        params: { ...filterParams(), page: p, limit: 500 },
      })
      const parsed = platformTenantsResponse.parse(res.data)
      all.push(...(parsed.data ?? []))
      const info = parsed.pagination
      if (!info || p >= info.totalPages) break
    }
    // "Export selected" passes the chosen ids — return just those rows so the
    // export honours the selection instead of dumping every company.
    if (scope === 'selected' && Array.isArray(ids)) {
      const wanted = new Set(ids.map(String))
      return all.filter((t) => wanted.has(String(t.id)))
    }
    return all
  }

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Company Name',
        grow: true,
        pinnable: true,
        minWidth: 200,
        sortAccessor: (t) => (t.name ?? '').toLowerCase(),
        exportValue: (t) => t.name ?? '',
        render: (t) => (
          <Link
            to={`/super-admin/companies/${t.id}`}
            title={`Open ${t.name} dashboard`}
            className="block text-sm font-semibold text-[color:var(--color-ink-strong)] truncate hover:text-[color:var(--color-accent-strong)] hover:underline underline-offset-2 cursor-pointer transition-colors"
          >
            {t.name}
          </Link>
        ),
      },
      {
        key: 'plan',
        header: 'Plan',
        minWidth: 130,
        sortAccessor: (t) => (t.planName ?? t.planTier ?? '').toLowerCase(),
        exportValue: (t) =>
          t.planName ?? (t.planTier ? 'Retired plan' : 'Awaiting plan'),
        render: (t) =>
          t.planName ? (
            <span className="inline-flex text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]">
              {t.planName}
            </span>
          ) : t.planTier ? (
            <span
              className="inline-flex text-xs uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger)] border border-[color:var(--color-danger-border)]"
              title={`This company is on plan code "${t.planTier}", which no longer exists in the plan catalogue. Reassign a current plan via Manage Plan.`}
            >
              Retired plan
            </span>
          ) : (
            <span
              className="inline-flex text-xs uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]"
              title="Awaiting plan selection — this self-signup company hasn't chosen a plan yet and is routed to Billing to pick one."
            >
              Awaiting plan
            </span>
          ),
      },
      {
        key: 'users',
        header: 'Users',
        minWidth: 90,
        sortAccessor: (t) => Number(t.memberCount ?? 0),
        exportValue: (t) => t.memberCount ?? 0,
        render: (t) => (
          <span
            className="text-sm text-[color:var(--color-ink-strong)] tabular-nums"
            title={
              t.pendingInviteCount > 0
                ? `${t.memberCount} total · ${t.pendingInviteCount} pending invitation${t.pendingInviteCount === 1 ? '' : 's'} — click to view`
                : `${t.memberCount} total — click to view`
            }
          >
            <button
              type="button"
              disabled={!t.memberCount}
              onClick={() => setUsersTenant({ id: t.id, name: t.name })}
              className={`tabular-nums ${
                t.memberCount
                  ? 'text-[color:var(--color-accent-strong)] hover:underline underline-offset-2 cursor-pointer'
                  : 'text-[color:var(--color-ink-muted)] cursor-default'
              }`}
            >
              {t.memberCount}
            </button>
            {t.pendingInviteCount > 0 ? (
              <span className="ml-1 text-[10px] text-[color:var(--color-ink-muted)] tabular-nums">
                ({t.pendingInviteCount} pending)
              </span>
            ) : null}
          </span>
        ),
      },
      {
        key: 'aiUsage',
        header: 'AI Usage',
        minWidth: 100,
        sortAccessor: (t) => Number(t.aiTokensThisMonth ?? 0),
        exportValue: (t) => t.aiTokensThisMonth ?? 0,
        cellClassName:
          'text-sm text-[color:var(--color-ink-strong)] tabular-nums whitespace-nowrap',
        render: (t) => (
          <span
            title={`${formatNumber(t.aiTokensThisMonth ?? 0)} estimated tokens this month`}
          >
            {fmtTokens(t.aiTokensThisMonth)}
          </span>
        ),
      },
      {
        key: 'storage',
        header: 'Storage',
        minWidth: 90,
        sortAccessor: (t) => Number(t.storageBytes ?? 0),
        exportValue: (t) => fmtBytes(t.storageBytes),
        cellClassName:
          'text-sm text-[color:var(--color-ink-strong)] tabular-nums whitespace-nowrap',
        render: (t) => fmtBytes(t.storageBytes),
      },
      {
        key: 'status',
        header: 'Company Status',
        minWidth: 120,
        sortAccessor: (t) => t.displayStatus ?? t.status ?? '',
        exportValue: (t) => t.displayStatus ?? t.status ?? '',
        render: (t) => {
          const cs = t.displayStatus ?? t.status
          return (
            <span
              className={
                'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold capitalize text-white border border-transparent dark:border-white/10 ' +
                (cs === 'active'
                  ? 'bg-[color:var(--color-success)] dark:bg-white/[0.06] dark:text-emerald-300'
                  : cs === 'invited'
                    ? 'bg-[color:var(--color-warning)] dark:bg-white/[0.06] dark:text-amber-300'
                    : cs === 'offboarded'
                      ? 'bg-[color:var(--color-ink-muted)] dark:bg-white/[0.06] dark:text-slate-300'
                      : 'bg-[color:var(--color-priority-urgent)] dark:bg-white/[0.06] dark:text-rose-300')
              }
            >
              {cs}
            </span>
          )
        },
      },
      {
        key: 'country',
        header: 'Country',
        minWidth: 120,
        sortAccessor: (t) => (t.country ?? '').toLowerCase(),
        exportValue: (t) => t.country ?? '',
        render: (t) => (
          <CountryCell country={t.country} billingCurrency={t.billingCurrency} />
        ),
      },
      {
        key: 'origin',
        header: 'Origin',
        minWidth: 120,
        // Sort by the LABEL the pill displays, not the raw createdVia enum.
        sortAccessor: (t) => {
          const v = String(t.createdVia ?? 'self_signup')
          if (v === 'super_admin') return 'admin created'
          if (v === 'demo_seed') return 'demo seed'
          return 'self signup'
        },
        exportValue: (t) => {
          const v = String(t.createdVia ?? 'self_signup')
          if (v === 'super_admin') return 'Admin created'
          if (v === 'demo_seed') return 'Demo seed'
          return 'Self signup'
        },
        render: (t) => <OriginCell createdVia={t.createdVia} />,
      },
      {
        key: 'createdAt',
        header: 'Created',
        minWidth: 130,
        sortAccessor: (t) => (t.createdAt ? new Date(t.createdAt) : null),
        exportValue: (t) => (t.createdAt ? formatDateTime(t.createdAt) : ''),
        cellClassName:
          'text-[color:var(--color-ink-muted)] text-xs whitespace-nowrap',
        render: (t) => formatDateTime(t.createdAt),
      },
      {
        key: 'actions',
        header: 'Actions',
        pinnedRight: true,
        align: 'center',
        minWidth: 72,
        sortable: false,
        exportable: false,
        hideable: false,
        render: (t) => <TenantRowActions tenant={t} />,
      },
    ],
    [],
  )

  return (
    <div>
      <Breadcrumb current="Companies" homePath="/super-admin/dashboard" />

      <div>
        {/* Heading row — subtitle now carries the company count
         *  inline (matches the reference design from the brief). We
         *  dropped the 4-tile stat strip because it was duplicating
         *  numbers already visible in the table + dashboard. */}
        {/* Stacks below `sm` so the action button is never pushed off the
         *  right edge on a phone. A flex item defaults to `min-width:auto`,
         *  so the heading block refuses to shrink past its content and the
         *  row (which does not wrap) overflows instead — `min-w-0` on the
         *  text and `shrink-0` on the button keep that from recurring at
         *  the in-between widths where the row is still horizontal. */}
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
              <Building2 size={20} className="text-[color:var(--color-accent-strong)] shrink-0" />
              Companies
            </h1>
            <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
              Manage every company.
            </p>
          </div>
          <Button className="shrink-0 self-start" onClick={() => setCreateOpen(true)}>
            <Plus size={14} />
            Create Company
          </Button>
        </div>

        {/* KPI strip — four cards giving an at-a-glance view of the
         *  tenant set currently being explored: Total · Active ·
         *  Inactive · Trial. Each card carries a 14-day signups
         *  sparkline painted at the bottom — the colour matches the
         *  icon-chip tone so the four cards read as a colour-coded
         *  strip. Numbers respect the SAME search / status / country
         *  / plan filters as the table, so as the user narrows, the
         *  cards update with the filtered view. "Total" reads as
         *  "how many companies match my current filter" rather than
         *  the platform total — clearing all filters returns the
         *  platform-wide rollup. */}
        <div className="mb-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            label="Total Companies"
            value={summary.total}
            icon={Building2}
            tone="violet"
          />
          <KpiCard
            label="Active"
            value={summary.active}
            icon={CheckCircle2}
            tone="emerald"
          />
          <KpiCard
            label="Inactive"
            value={summary.inactive}
            icon={PauseCircle}
            tone="rose"
            hint="Suspended + Offboarded"
          />
          <KpiCard
            label="Trial"
            value={summary.trial}
            icon={Clock3}
            tone="amber"
            hint="Companies on a trial plan"
          />
        </div>

        {/* Filters — sit directly above the table now that the stat
         *  strip is gone. Compact: search + selects via the shared
         *  FilterRow primitive. CountryFilter retains its custom
         *  popover and sits as a sibling. */}
        <FilterRow>
          <FilterRow.Search
            value={search}
            onChange={(v) => {
              setSearch(v)
              setPage(1)
            }}
            placeholder="Search by Company Name"
          />
          <FilterRow.Select
            ariaLabel="Status"
            placeholder="All statuses"
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v)
              setPage(1)
            }}
            options={[
              { value: 'all', label: 'All statuses' },
              { value: 'active', label: 'Active' },
              // Derived status — an active company whose owner hasn't
              // accepted their invite yet. Shown in the Company Status
              // column, so it has to be filterable too (QA-reported).
              { value: 'invited', label: 'Invited' },
              { value: 'suspended', label: 'Suspended' },
              { value: 'offboarded', label: 'Offboarded' },
            ]}
          />
          {/* Country filter — full ~245-country picker (replaces the
           *  earlier 3-option Select). Shows SVG flag + name + dial
           *  code per row and supports search-as-you-type. Picking a
           *  country narrows the table to tenants whose billing
           *  country matches. Resets page to 1 on change so the user
           *  lands at the first row of the filtered set instead of a
           *  stale deeper page. */}
          <CountryFilter
            value={countryFilter}
            onChange={(next) => {
              setCountryFilter(next)
              setPage(1)
            }}
          />
          <FilterRow.Select
            ariaLabel="Plan"
            placeholder="All plans"
            value={planFilter}
            onChange={(v) => {
              setPlanFilter(v)
              setPage(1)
            }}
            options={[
              { value: 'all', label: 'All plans' },
              ...planOptions,
            ]}
          />
          <FilterRow.Spacer />
          <FilterRow.Clear show={hasAnyFilter} onClick={clearFilters} />
          {/* Manual refresh — matches Storage / AI Usage / Coupons /
           *  Plans / SoftwareWatch. This is the ONLY way to pull in a
           *  change made elsewhere without a full page reload: the app
           *  sets `refetchOnWindowFocus: false` globally (App.jsx), so
           *  the list does NOT refresh itself when the tab regains
           *  focus. (An earlier version of this comment claimed it did.) */}
          <FilterRow.Refresh
            onClick={() => tenantsQuery.refetch()}
            loading={tenantsQuery.isFetching}
          />
        </FilterRow>

        {/* Server-paginated companies roster on the shared DataTable. Search +
         *  Status + Country + Plan filters and the KPI strip above stay
         *  page-owned (server params + server `summary`), so DataTable's own
         *  search is suppressed (`searchable={false}`). Sort reorders the
         *  current page; the Actions column renders TenantRowActions (kebab).
         *  `getRowProps` re-adds the dashboard deep-link focus ring +
         *  `data-tenant-id` hook the focus effect above scrolls to. */}
        <DataTable
          tableId="platform-tenants"
          columns={columns}
          rows={rows}
          getRowId={(t) => t.id}
          getRowProps={(t) => ({
            'data-tenant-id': t.id,
            className:
              focusedId === t.id
                ? 'bg-amber-50 dark:bg-amber-500/10 ring-2 ring-amber-300 dark:ring-amber-500/30'
                : undefined,
          })}
          loading={tenantsQuery.isLoading}
          fetching={tenantsQuery.isFetching}
          error={
            tenantsQuery.isError
              ? extractApiError(tenantsQuery.error).message
              : null
          }
          onRetry={() => tenantsQuery.refetch()}
          searchable={false}
          defaultSort={[{ key: 'createdAt', dir: 'desc' }]}
          page={pageInfo?.page ?? page}
          totalPages={pageInfo?.totalPages ?? 1}
          total={pageInfo?.total ?? 0}
          limit={pageInfo?.limit ?? limit}
          onPageChange={setPage}
          onLimitChange={(n) => {
            setLimit(n)
            setPage(1)
          }}
          exportBaseName="companies"
          exportTitle="Companies"
          getExportRows={fetchAllForExport}
          selectable
          bulkActions={bulkActions}
          onSelectAllRecords={async () =>
            (await fetchAllForExport()).map((t) => t.id)
          }
          emptyState={{
            icon: Building2,
            title: 'No records found',
          }}
        />
      </div>

      {/* Bulk-action confirm — reason capture for Suspend/Offboard, explicit
          danger copy for the terminal (Offboard) + permanent (Delete) ops. */}
      {bulk
        ? (() => {
            const cfg = BULK[bulk.type]
            const known = bulk.ids.filter((id) => statusById[id] != null)
            const unknown = bulk.ids.length - known.length
            const applicable =
              known.filter((id) => cfg.applies(statusById[id])).length + unknown
            const skipped = bulk.ids.length - applicable
            const label = `${applicable} ${applicable === 1 ? 'company' : 'companies'}`
            return (
              <Modal
                open
                onClose={closeBulk}
                size="sm"
                title={`${cfg.verb} ${label}`}
                footer={
                  <>
                    <Button
                      variant="secondary"
                      onClick={closeBulk}
                      disabled={bulkRunning}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant={cfg.danger ? 'danger' : 'primary'}
                      loading={bulkRunning}
                      disabled={applicable === 0}
                      onClick={runBulk}
                    >
                      {cfg.verb}
                    </Button>
                  </>
                }
              >
                <div className="space-y-3 text-sm text-[color:var(--color-ink)]">
                  <p className="break-words [overflow-wrap:anywhere]">
                    {applicable > 0
                      ? `This will ${cfg.verb.toLowerCase()} ${label}.`
                      : 'None of the selected companies can take this action.'}
                    {skipped > 0
                      ? ` ${skipped} will be skipped (the action doesn’t apply).`
                      : ''}
                  </p>
                  {bulk.type === 'offboard' ? (
                    <p className="text-[color:var(--color-danger)]">
                      Offboarding is terminal — each company is archived and all
                      its members are signed out.
                    </p>
                  ) : null}
                  {bulk.type === 'delete' ? (
                    <p className="text-[color:var(--color-danger)]">
                      This permanently deletes the selected companies and cannot
                      be undone.
                    </p>
                  ) : null}
                  {cfg.reason ? (
                    <textarea
                      rows={3}
                      value={bulkReason}
                      onChange={(e) => setBulkReason(e.target.value)}
                      placeholder="Reason (optional) — shown to affected members"
                      className="w-full rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 py-2 text-sm outline-none focus:border-[color:var(--color-accent)]"
                    />
                  ) : null}
                </div>
              </Modal>
            )
          })()
        : null}

      <TenantCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />

      <CompanyUsersModal
        tenant={usersTenant}
        onClose={() => setUsersTenant(null)}
      />
    </div>
  )
}

/** Per-company users popup — opened from the Companies table's "Users"
 *  count. Lists every user in the company (name, email, role, work role,
 *  status) via the tenant-scoped /super-admin/platform/users endpoint. */
function CompanyUsersModal({ tenant, onClose }) {
  const open = Boolean(tenant)
  const query = useQuery({
    enabled: open,
    queryKey: ['platform-company-users', tenant?.id],
    queryFn: async () => {
      const res = await api.get('/super-admin/platform/users', {
        params: { tenantId: tenant.id, limit: 100 },
      })
      return platformUsersResponse.parse(res.data)
    },
  })
  const rows = query.data?.data ?? []

  return (
    <Modal
      open={open}
      onClose={onClose}
      // `xl` (896px) — was `ml` (576px) which was too narrow once the
      // user list switched from a flex stack to a 4-column table.
      // Column min-widths total ~680px, so anything under that crushed
      // the table or triggered horizontal scroll inside the modal.
      // The Modal's own `max-h-[90vh]` handles tall content; we don't
      // need to override the vertical cap.
      size="xl"
      title={tenant ? `${tenant.name} · Users` : 'Users'}
      description="Everyone in this company."
    >
      {/* `min-h-[480px]` so even a 2-row company feels like a proper
       *  panel, not a cramped tooltip. The Modal's outer `max-h-[90vh]`
       *  still caps tall content + flips the body to a scroll region. */}
      <div className="min-h-[480px]">
      {query.isLoading ? (
        <p className="py-6 text-center text-sm text-[color:var(--color-ink-muted)]">
          Loading users…
        </p>
      ) : query.isError ? (
        <p className="py-6 text-center text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(query.error).message}
        </p>
      ) : rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-[color:var(--color-ink-muted)]">
          No users in this company yet.
        </p>
      ) : (
        // Proper `<table>` layout (was a flex-based `<ul>` of `<li>`s
        // where columns drifted when names were long and the Work Role
        // truncated unpredictably). Wrapped in `overflow-x-auto` so the
        // 4 column min-widths can overflow the modal on narrow viewports
        // instead of crushing the cells. `border-separate` lets each
        // header cell keep its own border treatment.
        <div className="overflow-x-auto -mx-1">
          <table
            className="w-full text-left text-sm border-separate border-spacing-0"
            style={{ minWidth: '680px' }}
          >
            <thead className="text-[color:var(--color-ink-muted)] font-medium">
              <tr>
                <Th className="min-w-[280px] border-b-2 border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]">
                  User
                </Th>
                <Th className="min-w-[140px] border-b-2 border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]">
                  Role
                </Th>
                <Th className="min-w-[120px] border-b-2 border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]">
                  Work Role
                </Th>
                <Th className="min-w-[100px] border-b-2 border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]">
                  Status
                </Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr
                  key={u.id}
                  className="hover:bg-[color:var(--color-canvas)] transition-colors"
                >
                  <Td className="border-b border-[color:var(--color-rule)]">
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar
                        name={u.fullName}
                        avatarUrl={u.avatarUrl ?? null}
                        size="md"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-[color:var(--color-ink-strong)] truncate m-0">
                          {u.fullName}
                        </p>
                        <p className="text-xs text-[color:var(--color-ink-muted)] truncate m-0 mt-0.5">
                          {u.emailId}
                        </p>
                      </div>
                    </div>
                  </Td>
                  <Td className="border-b border-[color:var(--color-rule)]">
                    <RolePill
                      {...(u.role === 'company_admin'
                        ? { label: 'Company Admin', tone: 'amber' }
                        : u.role === 'sub_admin'
                          ? { label: 'Sub Admin', tone: 'amber' }
                          : { label: 'User', tone: 'violet' })}
                    />
                  </Td>
                  <Td className="text-[color:var(--color-ink)] border-b border-[color:var(--color-rule)]">
                    {u.role === 'user'
                      ? (u.workRoleName ?? 'Employee')
                      : (
                        <span className="text-[color:var(--color-ink-subtle)]">
                          —
                        </span>
                      )}
                  </Td>
                  <Td className="border-b border-[color:var(--color-rule)]">
                    <StatusPill
                      status={u.status}
                      companyStatus={u.tenantStatus}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </Modal>
  )
}

/** Header cell — matches the WorkspacesPage `Th` so both admin tables
 *  read with the same typographic rhythm. */
function Th({ children, className = '' }) {
  return (
    <th
      className={`px-4 py-3 font-medium text-sm ${className}`}
    >
      {children}
    </th>
  )
}

/** Body cell — same as Workspaces. */
function Td({ children, className = '', colSpan }) {
  return (
    <td colSpan={colSpan} className={`px-4 py-3 align-middle ${className}`}>
      {children}
    </td>
  )
}
